import { getOauth2Token } from './getOauth2Token'
import { DEFAULT_OAUTH_SCOPES } from '@learnics/models/src/utils/oauthConfig.mjs'

/**
 * Checks to see if the user has an actively valid GAPI token with the required scopes.  If not, it will return the
 * scopes that are missing.  If the user is not logged in, it will return all the scopes.
 *
 * If the token needed to be refreshed, and was found to still be valid, it will return the refreshed token as `newGapiToken`.
 *
 * @param scopes - An array of scopes to check for
 * @param currentUserId - The current user's ID (optional)
 * @param token - The current user's gapi token (optional)
 * @returns {Promise<{missingScopes: *[]}|{missingScopes: *[], newGapiToken: *}>} - An object with the missing scopes, and optionally a new gapi token
 */
export async function validateTokenForScopes(
  providerId,
  scopes = null,
  currentUserId = null,
  token = null,
) {
  scopes ||= []
  scopes = [...scopes]
  const defaultScopes = DEFAULT_OAUTH_SCOPES[providerId] || []
  scopes = [...scopes, ...defaultScopes]
  const missingScopes = []
  let needsRefresh = false
  try {
    if (currentUserId) {
      if (!token) {
        token = await getOauth2Token(currentUserId, providerId)
      }
      if (token) {
        // User is logged in and has a gapi token.. check the token to see if we even need to request more scopes
        const existingScopes = new Set(token.scope.split(' '))
        for (let scope of scopes) {
          if (Array.isArray(scope)) {
            let matchesAnySubScope = false
            for (let subScope of scope) {
              matchesAnySubScope ||= existingScopes.has(subScope)
            }
            if (!matchesAnySubScope) {
              missingScopes.push(scope)
            }
          } else if (!existingScopes.has(scope)) {
            missingScopes.push(scope)
          }
        }
        if (missingScopes.length === 0) {
          // All the scopes have already been authorized.  We don't need to do the auth flow again!

          // if the token expires before 10 seconds from now...
          if (token.expiry_date < Date.now() + 10000) {
            // Token needs refreshed
            needsRefresh = true
          }
        }
      } else {
        // User is logged in but has no gapi token.  Request the minimal amount of scopes required, as they will be
        // progressively requested as needed.
        missingScopes.push(...scopes)
      }
    } else {
      missingScopes.push(...scopes)
    }
  } catch (err) {
    console.log('Error validating gapi token for scopes', err)
    missingScopes.push(...scopes)
  }
  // It is up to the caller to decide what to do after this.
  return {
    missingScopes: [
      ...new Set(
        missingScopes.map((scope) => {
          if (Array.isArray(scope)) {
            return scope[0] // Return the first scope in the array, presumably the least privileged
          } else {
            return scope
          }
        }),
      ),
    ],
    token,
    needsRefresh,
  }
}
